import { jsx as _jsx } from "react/jsx-runtime";
import { CardAdvancement } from '@cta-portal/webapp-common/lib/api/schema';
import { useI18n } from '@cta-portal/webapp-common/lib/hooks/translations/use-i18n';
import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { MarketDetailsTab, RoutePath } from '../../app/router/config';
import buyIcon from '../../assets/images/icons/common/icon-buy-dark.png';
import numberingIcon from '../../assets/images/icons/common/icon-numbering-gold.png';
import seeIcon from '../../assets/images/icons/common/icon-reveal-full-purple.png';
import shredIcon from '../../assets/images/icons/common/icon-shred-gold.png';
import { NavHistoryContext } from '../../context/nav-history';
import { UserContext } from '../../context/user';
import { TokenType } from '../../types/common';
import { elementAssets, foilAssets, gradeAssets, rankAssets } from '../../utils/cards-metadata-assets';
import { formatDate } from '../../utils/date';
import Table, { CellAlign, CellType } from '../common/table';
export default function OrdersTable(_a) {
    var activity = _a.activity, orders = _a.orders, tokenType = _a.tokenType, advancement = _a.advancement, selectedOrderIds = _a.selectedOrderIds, buyAction = _a.buyAction, selectOrder = _a.selectOrder, shiftSelectOrder = _a.shiftSelectOrder, selectAllOrders = _a.selectAllOrders;
    var _b = useI18n(), t = _b.t, i18n = _b.i18n;
    var location = useLocation();
    var selectedWallet = useContext(UserContext).selectedWallet;
    var addHistory = useContext(NavHistoryContext).addHistory;
    var labels = [
        {
            key: 'date',
            label: t('common.date'),
            align: CellAlign.LEFT,
            size: 1.2,
            mobileSize: 1,
            hidden: !activity,
        },
        {
            key: 'numbering',
            label: t('common.numbering'),
            icon: numberingIcon,
            align: CellAlign.CENTER,
            hiddenMobile: true,
            showExtendMobile: true,
            hidden: tokenType !== TokenType.CARD && tokenType !== TokenType.SOUVENIR_CARD && tokenType !== TokenType.MINT_PASS,
        },
        {
            key: 'foil',
            label: t('filters.foil'),
            align: CellAlign.CENTER,
            size: 0.5,
            hiddenMobile: true,
            showExtendMobile: true,
            hidden: tokenType !== TokenType.CARD && tokenType !== TokenType.SOUVENIR_CARD,
        },
        {
            key: 'element',
            label: t('filters.element'),
            align: CellAlign.CENTER,
            size: 0.5,
            hiddenMobile: true,
            showExtendMobile: true,
            hidden: advancement !== CardAdvancement.COMBO || tokenType !== TokenType.CARD,
        },
        {
            key: 'rank',
            label: t('filters.rank'),
            align: CellAlign.CENTER,
            size: 0.5,
            hidden: advancement !== CardAdvancement.STANDARD || tokenType !== TokenType.CARD,
        },
        {
            key: 'grade',
            label: t('filters.grade'),
            align: CellAlign.CENTER,
            size: 0.5,
            hidden: advancement === CardAdvancement.STANDARD ||
                (tokenType !== TokenType.CARD && tokenType !== TokenType.SOUVENIR_CARD),
        },
        {
            key: 'shred',
            icon: shredIcon,
            label: t('common.shred'),
            align: CellAlign.CENTER,
            size: 1,
            hiddenMobile: true,
            showExtendMobile: true,
        },
        { key: 'price', label: t('common.price'), align: CellAlign.RIGHT, size: 1.2 },
        {
            key: 'action',
            label: t('common.actions'),
            align: CellAlign.CENTER,
            size: 1.2,
            actionCell: true,
            hidden: activity,
        },
    ];
    var actions = [
        {
            icon: seeIcon,
            link: {
                routePath: RoutePath.ASSET,
                generateParams: function (id) {
                    var order = orders.find(function (order) { return order.order.order_id.toString() === id; });
                    return {
                        tokenId: (order === null || order === void 0 ? void 0 : order.order.sell.data.token_id) || '',
                        tab: MarketDetailsTab.OVERVIEW,
                    };
                },
            },
            action: function (_id) { return addHistory(location); },
            generateInvisibility: function (_id) { return false; },
        },
    ];
    if (buyAction) {
        actions.push({
            highlighted: true,
            icon: buyIcon,
            action: function (id) { return buyAction(Number.parseInt(id)); },
        });
    }
    function getNumbering(order) {
        var _a, _b, _c;
        if (tokenType === TokenType.CARD) {
            return ((_a = order.card) === null || _a === void 0 ? void 0 : _a.numbering) || 0;
        }
        if (tokenType === TokenType.SOUVENIR_CARD) {
            return ((_b = order.souvenirCard) === null || _b === void 0 ? void 0 : _b.numbering) || 0;
        }
        if (tokenType === TokenType.MINT_PASS) {
            return ((_c = order.mintPass) === null || _c === void 0 ? void 0 : _c.numbering) || 0;
        }
        return 0;
    }
    function getFoil(order) {
        var _a, _b;
        if (tokenType === TokenType.CARD) {
            return (_a = order.card) === null || _a === void 0 ? void 0 : _a.foil;
        }
        if (tokenType === TokenType.SOUVENIR_CARD) {
            return (_b = order.souvenirCard) === null || _b === void 0 ? void 0 : _b.foil;
        }
        return undefined;
    }
    function getGrade(order) {
        var _a, _b;
        if (tokenType === TokenType.CARD) {
            return (_a = order.card) === null || _a === void 0 ? void 0 : _a.grade;
        }
        if (tokenType === TokenType.SOUVENIR_CARD) {
            return (_b = order.souvenirCard) === null || _b === void 0 ? void 0 : _b.grade;
        }
        return undefined;
    }
    var rows = orders.map(function (order) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        var numbering = getNumbering(order);
        var foil = getFoil(order);
        var grade = getGrade(order);
        var shred = order.shredPreview !== undefined && order.shredPreview.stonesCount > 0 && ((_a = order.price.usd) === null || _a === void 0 ? void 0 : _a.value)
            ? (order.price.usd.value / order.shredPreview.stonesCount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 3,
            })
            : undefined;
        return {
            id: order.order.order_id.toString(),
            disabled: !activity && selectedWallet && selectedWallet.address === order.order.user,
            data: {
                date: {
                    type: CellType.TEXT,
                    value: formatDate(order.order.updated_timestamp || '', i18n.language),
                },
                numbering: {
                    type: CellType.NUMBERING,
                    value: numbering,
                    tokenId: order.order.sell.data.token_id || '',
                    tokenAddress: order.order.sell.data.token_address || '',
                },
                foil: {
                    type: CellType.METADATA,
                    value: {
                        label: foil ? t('common.yes') : t('common.no'),
                        icon: foil ? foilAssets.true : foilAssets.false,
                    },
                },
                element: {
                    type: CellType.METADATA,
                    value: {
                        label: ((_b = order.card) === null || _b === void 0 ? void 0 : _b.element) ? t("card-metadata.element.".concat(order.card.element.toLowerCase())) : '',
                        icon: ((_c = order.card) === null || _c === void 0 ? void 0 : _c.element) ? elementAssets[order.card.element] : '',
                    },
                },
                rank: {
                    type: CellType.METADATA,
                    value: {
                        label: "".concat(t('common.rank'), " ").concat((_d = order.card) === null || _d === void 0 ? void 0 : _d.rank),
                        icon: ((_e = order.card) === null || _e === void 0 ? void 0 : _e.rank) ? rankAssets[order.card.rank] : '',
                    },
                },
                grade: {
                    type: CellType.METADATA,
                    value: {
                        label: "".concat(t('common.grade'), " ").concat(grade),
                        icon: grade ? gradeAssets[grade] : '',
                    },
                },
                shred: {
                    type: CellType.SHRED_PREVIEW,
                    value: shred,
                },
                price: {
                    type: CellType.PRICE,
                    value: {
                        tokenSymbol: (_f = order.price.base) === null || _f === void 0 ? void 0 : _f.symbol,
                        value: ((_g = order.price.base) === null || _g === void 0 ? void 0 : _g.displayValue) || '0',
                        usdValue: ((_h = order.price.usd) === null || _h === void 0 ? void 0 : _h.displayValue) || '0',
                    },
                },
            },
        };
    });
    return (_jsx(Table, { actions: actions, labels: labels, notRounded: true, rows: rows, selectAllRows: selectAllOrders, selectRow: selectOrder, selectedRowIds: selectedOrderIds, shiftSelectRow: shiftSelectOrder }));
}
